import { useEffect } from 'react';
import { DEFAULT_OG_IMAGE, DEFAULT_OG_IMAGE_HEIGHT, DEFAULT_OG_IMAGE_WIDTH, SITE_NAME, SITE_URL } from '../utils/seo';

const MANAGED_SELECTOR = 'data-seo-managed';

function upsertMeta(attr, key, content) {
  if (content == null || content === '') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"][${MANAGED_SELECTOR}]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute(MANAGED_SELECTOR, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href, extra = {}) {
  if (!href) return;
  const hreflang = extra.hreflang;
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"][${MANAGED_SELECTOR}]`
    : `link[rel="${rel}"][${MANAGED_SELECTOR}]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(MANAGED_SELECTOR, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  Object.entries(extra).forEach(([key, value]) => {
    if (value != null) el.setAttribute(key, value);
  });
}

function upsertJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.setAttribute(MANAGED_SELECTOR, 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function clearManagedSeo() {
  document.querySelectorAll(`[${MANAGED_SELECTOR}]`).forEach((node) => node.remove());
}

export default function SeoHead({
  title = SITE_NAME,
  description = '',
  keywords = '',
  image = DEFAULT_OG_IMAGE,
  url = SITE_URL,
  type = 'website',
  noindex = false,
  language = 'fr',
  jsonLd = null,
}) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const robots = noindex ? 'noindex, nofollow' : 'index, follow';
    const canonical = url.endsWith('/') && url !== `${SITE_URL}/` ? url.replace(/\/$/, '') : url;
    const absoluteImage = image?.startsWith('http') ? image : `${SITE_URL}${image?.startsWith('/') ? image : `/${image || 'preview.png'}`}`;

    document.documentElement.lang = language;
    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', robots);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:image', absoluteImage);
    if (absoluteImage.includes('/preview.png') || absoluteImage.includes('/logo512.png')) {
      upsertMeta('property', 'og:image:width', String(DEFAULT_OG_IMAGE_WIDTH));
      upsertMeta('property', 'og:image:height', String(DEFAULT_OG_IMAGE_HEIGHT));
      upsertMeta('property', 'og:image:alt', `${SITE_NAME} logo`);
    }
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', language === 'ar' ? 'ar_MA' : 'fr_FR');
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', absoluteImage);

    upsertLink('canonical', canonical);
    upsertLink('alternate', canonical, { hreflang: 'fr' });
    upsertLink('alternate', canonical, { hreflang: 'ar' });
    upsertLink('alternate', canonical, { hreflang: 'x-default' });

    upsertJsonLd('seo-json-ld', jsonLd);

    return () => {
      clearManagedSeo();
    };
  }, [title, description, keywords, image, url, type, noindex, language, jsonLdKey]);

  return null;
}
